import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom'; 

const AccountSettings = ({ loggedInUser, setLoggedInUser }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false); // New State: Toggle Edit Mode
  const [error, setError] = useState(""); // <--- NEW: Error state
  // --- NEW STATES FOR SUBSCRIPTION VIEW ---
  const [isCheckingSub, setIsCheckingSub] = useState(false);
  const [subData, setSubData] = useState(null);
  const [isLoadingSub, setIsLoadingSub] = useState(false);
  const settingsRef = useRef(null);
  const navigate = useNavigate();

  // Local state for form inputs
  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    email: "",
    zipcode: ""
  });
  // --- NEW: Regex Constants ---
  const REGEX = {
    firstName: /^[A-Za-z]{1,15}$/,
    lastName: /^[A-Za-z]{1,20}$/,
    email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/ // Standard email check (requires an @ and a .)
  };


  useEffect(() => {
    const handleClickOutside = (event) => {
      if (settingsRef.current && !settingsRef.current.contains(event.target)) {
        setIsOpen(false);
        setIsEditing(false);
        setIsCheckingSub(false);
        setError(""); // <--- NEW: Clear errors when clicking away
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Auto-fetch subscription/trial status the first time the panel opens.
  // We intentionally do not re-fetch on every open to avoid hammering the API;
  // a page reload resets subData if the user wants a fresh check.
  useEffect(() => {
    if (isOpen && !subData && !isLoadingSub) {
      handleCheckSubscription();
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps
  // Pre-fill form when user opens settings
  useEffect(() => {
    if (loggedInUser) {
      setFormData({
        firstname: loggedInUser.firstname || "",
        lastname: loggedInUser.lastname || "",
        email: loggedInUser.email || "",
        zipcode: loggedInUser.zipcode || ""
      });
    }
  }, [loggedInUser]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (settingsRef.current && !settingsRef.current.contains(event.target)) {
        setIsOpen(false);
        setIsEditing(false); // Reset edit mode on close
        setIsCheckingSub(false); // Reset sub view on close
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // --- THE HOT SWAP HANDLER ---
  const handleUpdateProfile = async () => {
    setError(""); // <--- NEW: Clear any old errors on new submission

    // --- NEW: Validation Checks ---
    if (!REGEX.firstName.test(formData.firstname.trim())) { 
      setError("First Name must be 1 word, max 15 letters."); 
      return; 
    }
    if (!REGEX.lastName.test(formData.lastname.trim())) { 
      setError("Last Name must be 1 word, max 20 letters."); 
      return; 
    }
    if (!REGEX.email.test(formData.email.trim())) { 
      setError("Please enter a valid email address."); 
      return; 
    }
    try {
      // 1. Send Data (POST matches the new route)
      // Path: /api (Nginx) -> /users (Server) -> /secure-profile-update (Router)
      const response = await fetch("https://vicktoria.ai/api/secure-profile-update", {
        method: "POST", 
        headers: { 
          "Content-Type": "application/json",
          // We must use the OLD token one last time to authorize the swap
          "Authorization": `Bearer ${localStorage.getItem('token')}`
        },
        credentials: "include", // CRITICAL: Allows cookies to be overwritten
        body: JSON.stringify(formData),
      });

      // 2. Parse Response
      let result;
      try {
          result = await response.json();
      } catch (e) {
          throw new Error("Server crashed or returned HTML (404/500)");
      }

      if (response.ok) {
        // 3. THE HANDOFF (Update Storage)
        // This is exactly what LoginFront.jsx does on success
        localStorage.setItem("token", result.token);
        localStorage.setItem("loggedInUser", JSON.stringify(result.user));
        
        // Update State (Visual Feedback)
        setLoggedInUser(result.user);
        setIsEditing(false);
        alert("Profile updated successfully!");

        // 4. THE HARD REFRESH (Re-Initialize App)
        // This forces AppMaster to read the NEW token/user from localStorage
        // and guarantees Navbar/Footer syncs start fresh.
        window.location.reload(); 
      } else {
        alert(result.error || "Update failed.");
      }
    } catch (err) {
      console.error("Update error:", err);
      alert("Error: " + err.message);
    }
  };

  const handleCancelSubscription = async () => {
    if (!loggedInUser?.stripeCustomerId) {
      // In a real app, use a custom modal instead of alert
      console.log("No subscription found.");
      return;
    }

    if (window.confirm("Are you sure you want to cancel your Vicktoria subscription?")) {
    try {
      const response = await fetch("https://vicktoria.ai/api/cancel-subscription", {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          // ADD THE TOKEN HERE
          "Authorization": `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ email: loggedInUser.email }),
      });

      const result = await response.json();

        if (response.ok) {
          // Update local state to immediately reflect the change
          const updatedUser = { 
            ...loggedInUser, 
            isSpecialPermission: false, 
            onPaidPlan: false 
          };
          setLoggedInUser(updatedUser);
          localStorage.setItem("loggedInUser", JSON.stringify(updatedUser));
          setIsOpen(false);
          setIsCheckingSub(false);
          navigate('/'); // BEFORE: setCurrentView('home');
          alert("Subscription cancelled successfully.");
        } else {
          alert(result.error || "Cancellation failed.");
        }
      } catch (err) {
        console.error("Cancellation error:", err);
        alert("Server error during cancellation.");
      }
    }
  };
  
  // --- NEW FETCH FUNCTION FOR SUBSCRIPTION STATUS ---
  const handleCheckSubscription = async () => {
    setIsCheckingSub(true);
    setIsLoadingSub(true);
    try {
      const response = await fetch("https://vicktoria.ai/api/subscription-status", {
        headers: {
          "Authorization": `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      setSubData(data);
    } catch (err) {
      console.error("Failed to fetch subscription status:", err);
    } finally {
      setIsLoadingSub(false);
    }
  };

  // --- NEW HANDLER FOR BILLING PORTAL ---
  const handleManageBilling = async () => {
    try {
      const response = await fetch("https://vicktoria.ai/api/create-portal-session", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      
      if (response.ok && data.url) {
        // Redirect the user's browser to the Stripe-hosted portal
        window.location.href = data.url; 
      } else {
        alert(data.error || "Failed to open billing portal.");
      }
    } catch (err) {
      console.error("Billing portal error:", err);
      alert("Server error while connecting to Stripe.");
    }
  };
  // Styles
  const triggerButtonStyle = {
    backgroundColor: 'transparent',
    color: '#FFFFFF',
    border: '1px solid rgba(255, 255, 255, 0.4)',
    borderRadius: '4px',
    padding: '4px 12px',
    fontSize: '13px',
    cursor: 'pointer',
    fontFamily: 'Inter, sans-serif',
    marginRight: '15px',
    transition: 'background-color 0.2s',
  };

  const dropdownStyle = {
    position: 'absolute',
    top: '45px',
    left: '50%',
    transform: 'translateX(-50%)', // <-- ADDED: Center align itself
    backgroundColor: '#FFFFFF',
    boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
    borderRadius: '8px',
    width: '280px',
    padding: '20px',
    zIndex: 1000001,
    color: '#333',
    display: isOpen ? 'block' : 'none',
    alignItems: 'center',
  };

  const inputStyle = {
    width: '100%',
    padding: '8px',
    marginBottom: '10px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    boxSizing: 'border-box'
  };

  return (
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }} ref={settingsRef}>

      {/* NEW: Mobile Override Style */}
    <style>{`
      @media (max-width: 800px) {
        .settings-dropdown {
          position: fixed !important;
          top: 30% !important;
          left: 50% !important;
          transform: translate(-50%, -50%) !important;
          width: 70% !important; /* Prevents overflow on small phones */
        }
      }
    `}</style>
      <button 
        className="account-settings-btn"
        style={triggerButtonStyle} 
        onClick={() => setIsOpen(!isOpen)}
      >
        {loggedInUser ? `Account Settings` : "Settings"}
      </button>

      {isOpen && (
        <div className="settings-dropdown" style={dropdownStyle}>
          <h4 style={{ margin: '0 0 15px 0', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>
            {isCheckingSub ? "Subscription Status" : isEditing ? "Edit Profile" : "Account Settings"}
          </h4>

          {isCheckingSub ? (
            // --- SUBSCRIPTION STATUS MODE ---
            <div>
              {isLoadingSub ? (
                <p style={{ textAlign: 'center', fontSize: '14px', margin: '20px 0' }}>Contacting Stripe...</p>
              ) : (
                <>
                  {(!subData || subData.status === 'none') && (
                    <p style={{ textAlign: 'center', fontSize: '14px', margin: '20px 0', fontWeight: 'bold' }}>No Active Subscription</p>
                  )}

                  {/* Pre-Stripe free trial */}
                  {(subData?.status === 'trial' || subData?.status === 'trialing') && (
                    <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                      <div style={{ backgroundColor: '#fff9e6', border: '1px solid #ffc107', padding: '10px', borderRadius: '4px', fontWeight: 'bold', marginBottom: '10px' }}>
                        Free Trial
                      </div>
                      <p style={{ fontSize: '14px' }}>{subData.daysLeft} day{subData.daysLeft !== 1 ? 's' : ''} remaining</p>
                    </div>
                  )}

                  {subData?.status === 'expired' && (
                    <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                      <div style={{ backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', padding: '10px', borderRadius: '4px', fontWeight: 'bold', marginBottom: '10px', color: '#721c24' }}>
                        Trial Expired
                      </div>
                      <p style={{ fontSize: '14px' }}>Your free trial has ended. Subscribe to continue access.</p>
                    </div>
                  )}

                  {subData?.status === 'active' && (
                    <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                      <div style={{ backgroundColor: '#D1DEE6', color: '#2B4B6F', padding: '10px', borderRadius: '4px', fontWeight: 'bold', marginBottom: '10px' }}>
                        Active Subscription{subData.planLabel ? ` — ${subData.planLabel}` : ''}
                      </div>
                      {subData.planCode === 'A' && subData.renewalDate ? (
                        <p style={{ fontSize: '14px' }}>
                          Renews {new Date(subData.renewalDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                        </p>
                      ) : (
                        <p style={{ fontSize: '14px' }}>{subData.daysLeft} day{subData.daysLeft !== 1 ? 's' : ''} until next billing</p>
                      )}
                    </div>
                  )}

                  {/* Manage Billing — not shown for employee (H) plan: no Stripe subscription */}
                  {subData?.status !== 'none' && loggedInUser?.stripeCustomerId && subData?.planCode !== 'H' && (
                    <button
                      style={{ width: '100%', padding: '8px', backgroundColor: '#4a7094', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', marginBottom: '10px' }}
                      onClick={handleManageBilling}
                    >
                      Manage Billing & Payment Methods
                    </button>
                  )}

                  {/* Cancel — not shown for employee (H) plan */}
                  {subData?.status !== 'none' && loggedInUser?.stripeCustomerId && subData?.planCode !== 'H' && (
                    <button
                      style={{ width: '100%', padding: '8px', backgroundColor: '#d9534f', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', marginBottom: '10px' }}
                      onClick={handleCancelSubscription}
                    >
                      Cancel Subscription
                    </button>
                  )}
                </>
              )}

              <button 
                onClick={() => setIsCheckingSub(false)}
                style={{ width: '100%', padding: '8px', backgroundColor: '#ccc', color: 'black', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
              >
                Back
              </button>
            </div>

          ) : isEditing ? (
            // --- EDIT MODE ---
            <div>
              <label style={{fontSize: '12px'}}>First Name</label>
              <input 
                style={inputStyle} 
                value={formData.firstname} 
                onChange={(e) => setFormData({...formData, firstname: e.target.value})}
              />
              
              <label style={{fontSize: '12px'}}>Last Name</label>
              <input 
                style={inputStyle} 
                value={formData.lastname} 
                onChange={(e) => setFormData({...formData, lastname: e.target.value})}
              />

              <label style={{fontSize: '12px'}}>Email</label>
              <input 
                style={inputStyle} 
                value={formData.email} 
                onChange={(e) => setFormData({...formData, email: e.target.value.toLowerCase()})}
              />

              {/* NEW: Error Display */}
              {error && (
                <p style={{ color: '#D0021B', fontSize: '13px', marginBottom: '10px', textAlign: 'center' }}>
                  {error}
                </p>
              )}

              <div style={{display: 'flex', gap: '10px', marginTop: '10px'}}>
                <button 
                  onClick={handleUpdateProfile}
                  style={{...triggerButtonStyle, backgroundColor: '#4CAF50', color: 'white', flex: 1, textAlign: 'center', margin: 0}}
                >
                  Save
                </button>
                <button 
                  onClick={() => setIsEditing(false)}
                  style={{...triggerButtonStyle, backgroundColor: '#ccc', color: 'black', flex: 1, textAlign: 'center', margin: 0}}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            // --- VIEW MODE ---
            <div>
                  <p style={{ fontSize: '14px', marginBottom: '5px' }}><strong>Name:</strong> {loggedInUser?.firstname} {loggedInUser?.lastname}</p>
              <p style={{ fontSize: '14px', marginBottom: '10px' }}><strong>Email:</strong> {loggedInUser?.email}</p>

              {/* Plan status — auto-fetched when the panel opens */}
              {isLoadingSub && (
                <p style={{ fontSize: '13px', color: '#888', textAlign: 'center', marginBottom: '12px' }}>Loading plan status...</p>
              )}
              {subData && !isLoadingSub && (
                <div style={{
                  backgroundColor: subData.status === 'active' ? '#D1DEE6' : '#fff9e6',
                  border: `1px solid ${subData.status === 'active' ? '#4a7094' : '#ffc107'}`,
                  borderRadius: '6px',
                  padding: '8px 12px',
                  marginBottom: '12px',
                  fontSize: '13px',
                  textAlign: 'center'
                }}>
                  {(subData.status === 'trial' || subData.status === 'trialing') && (
                    <><strong>Free Trial</strong><br />{subData.daysLeft} day{subData.daysLeft !== 1 ? 's' : ''} remaining</>
                  )}
                  {subData.status === 'active' && (
                    <>
                      <strong>Active{subData.planLabel ? ` — ${subData.planLabel}` : ''}</strong><br />
                      {subData.planCode === 'A' && subData.renewalDate
                        ? `Renews ${new Date(subData.renewalDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                        : `${subData.daysLeft} day${subData.daysLeft !== 1 ? 's' : ''} until next billing`
                      }
                    </>
                  )}
                  {subData.status === 'expired' && (
                    <><strong style={{ color: '#856404' }}>Free Trial Expired</strong><br />Subscribe to continue access</>
                  )}
                  {subData.status === 'none' && (
                    <strong style={{ color: '#856404' }}>No Active Plan</strong>
                  )}
                </div>
              )}

              {/* Manage Billing shortcut for paid subscribers */}
              {loggedInUser?.stripeCustomerId && (
                <button
                  onClick={handleCheckSubscription}
                  style={{ width: '100%', padding: '8px', backgroundColor: '#4a7094', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', marginBottom: '10px' }}
                >
                  Manage Billing
                </button>
              )}
              
              <button 
                onClick={() => setIsEditing(true)}
                style={{ width: '100%', padding: '8px', marginBottom: '10px', cursor: 'pointer' }}
              >
                Edit Profile Info
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AccountSettings;